// src/components/Inquiry/EmailPreviewPanel.tsx
//
// The supplier email, shown as it will arrive. It is a whole HTML document
// with its own styles, so it renders in an iframe rather than being injected
// into the dialog.
//
// The email is authored at a fixed width - Layout.html.jinja2 caps it at 700px
// and lays it out on a 487.5pt (650px) table - and the column it is previewed
// in is narrower than that. Every earlier attempt let the email's own size
// reach the layout: the frame was sized from the rendered document, so
// whenever that measurement was missing or late, the column's min-content
// became the email's real width, which beats the Grid's max-width, and the
// preview took its room from the actions column beside it.
//
// So the preview no longer has an opinion about layout. The box is a fixed
// size the caller decides, it clips, and the email is drawn inside it at its
// design width and scaled down - a thumbnail, not a document. Nothing about
// the email can move anything on the page, whatever the scale does. Reading it
// properly happens in the full-size dialog, where it has the room to be its
// own width.
import { Download, OpenInFull } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FunctionComponent, ReactNode, useEffect, useState } from "react";

/**
 * The width the email is drawn at. Layout.html.jinja2 caps the mail itself at
 * 700px; the rest is room for the frame's own vertical scrollbar, which
 * otherwise eats into those 700 and leaves the mail scrolling sideways by the
 * width of a scrollbar. It is a constant on purpose: a measured width is a
 * width that can be missing. Exported because the column the preview sits in
 * is built to it, so the email shows at its own size instead of being scaled
 * to whatever is left over.
 */
export const EMAIL_WIDTH = 740;

/**
 * The email at its design width, in a box that is whatever size the column
 * gives it. The email scrolls inside the frame when it is taller than the box;
 * the box never grows to the email, so nothing about the document reaches the
 * layout.
 */
const EmailThumbnail: FunctionComponent<{
  html: string;
  title: string;
  minHeight: number;
}> = ({ html, title, minHeight }) => {
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  // The box is sized by the dialog, so both axes move: the width with the
  // window, the height with whatever the tallest column holds. Zero on either
  // axis only means the email is drawn at 1:1 and filling the box - the box is
  // the same size either way, so nothing on the page depends on this arriving.
  useEffect(() => {
    if (!box) return;
    setSize({ width: box.clientWidth, height: box.clientHeight });
    const observer = new ResizeObserver(([entry]) =>
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      })
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, [box]);

  // 1 wherever the column can hold EMAIL_WIDTH, which is what it is built to
  // do. Below that the email shrinks rather than being cut off.
  const scale = size.width ? Math.min(1, size.width / EMAIL_WIDTH) : 1;

  return (
    <div
      ref={setBox}
      style={{
        // The email's own width, and no wider. maxWidth keeps it inside a
        // column too narrow to hold it - there it scales down instead, which
        // is the only case the scale above is for.
        width: EMAIL_WIDTH,
        maxWidth: "100%",
        // Takes the height the column has left. These lines are what keep the
        // email out of the layout: the box cannot grow to the document, and
        // the document cannot contribute a min-content size through it.
        flex: 1,
        minHeight,
        overflow: "hidden",
        minWidth: 0,
        border: "1px solid var(--black10)",
        borderRadius: 4,
        background: "#fff",
      }}
    >
      <iframe
        title={title}
        srcDoc={html}
        // Nothing here reads the document, so the frame can stay fully
        // isolated: no scripts, and its own origin.
        sandbox=""
        style={{
          display: "block",
          border: 0,
          width: EMAIL_WIDTH,
          // Fills the box at this scale. A taller email scrolls inside the
          // frame - which is the one thing the frame is left able to do, and
          // why it is not pointer-events: none.
          height: size.height ? size.height / scale : "100%",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
};

/**
 * The email at its own width, in a dialog of its own. Reached from the
 * thumbnail's "Open full size", and on its own from the actions column once
 * the inquiry is in Pickup / Delivery - there the thumbnail is gone, the
 * goods-in counts have its column, and this is how the purchase order is
 * still read. `actions` sits in the title bar beside the close button;
 * `children` replaces the frame while there is nothing to show yet.
 */
export const EmailFullSizeDialog: FunctionComponent<{
  open: boolean;
  onClose: () => void;
  html: string;
  title: string;
  actions?: ReactNode;
  children?: ReactNode;
}> = ({ open, onClose, html, title, actions, children }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
    <DialogTitle
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>{title}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {actions}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </span>
    </DialogTitle>
    <DialogContent dividers sx={{ p: children ? 2 : 0 }}>
      {children ?? (
        // Its own dialog, so the email is finally wider than it needs to be:
        // no scaling, and the height is the dialog's, not the document's.
        <iframe
          title={`${title} (full size)`}
          srcDoc={html}
          sandbox=""
          style={{
            display: "block",
            border: 0,
            width: "100%",
            height: "75vh",
            background: "#fff",
          }}
        />
      )}
    </DialogContent>
  </Dialog>
);

type EmailPreviewProps = {
  html: string;
  title: string;
  /**
   * How short the preview may get before it stops giving room back. Tall by
   * default: an email is meant to be read here, and the dialog scrolls.
   */
  minHeight?: number;
};

/**
 * The preview as the enquiry dialog shows it: a thumbnail with a way to open
 * the email full size. Both halves of the dialog use this - the create form
 * previews the form, the saved enquiry previews itself by id.
 */
export const EmailPreview: FunctionComponent<EmailPreviewProps> = ({
  html,
  title,
  minHeight = 1000,
}) => {
  const [full, setFull] = useState(false);

  // A preview that has been replaced while its full-size view is open would
  // otherwise keep showing the old email until it is closed.
  useEffect(() => setFull(false), [html]);

  return (
    <div
      style={{
        // Takes the height its column was given and hands what is left to the
        // frame, so the preview is as tall as the dialog rather than a fixed
        // guess. minHeight: 0 is what lets it shrink instead of forcing the
        // column open at the frame's height.
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <EmailThumbnail html={html} title={title} minHeight={minHeight} />
      <Button
        size="small"
        startIcon={<OpenInFull fontSize="small" />}
        onClick={() => setFull(true)}
        sx={{ mt: 0.5, alignSelf: "flex-start" }}
      >
        Open full size
      </Button>

      <EmailFullSizeDialog
        open={full}
        onClose={() => setFull(false)}
        html={html}
        title={title}
      />
    </div>
  );
};

type EmailPreviewPanelProps = {
  html: string;
  isOpen: boolean;
  missingReason?: string;
  downloadPDF: () => void;
};

const EmailPreviewPanel: FunctionComponent<EmailPreviewPanelProps> = ({
  html,
  isOpen,
  missingReason,
  downloadPDF,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minWidth: 0,
      minHeight: 0,
    }}
  >
    {/* HTML comes from internal render API — acceptable for this admin tool */}
    {html && (
      <>
        {/* Was pinned to the dialog's own top-right corner, where it sat over
            the close button rather than over the thing it downloads. */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title="Download as PDF">
            <IconButton size="small" onClick={downloadPDF}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <EmailPreview html={html} title="Email preview" />
      </>
    )}
    {!isOpen && missingReason && (
      <Typography
        sx={{
          textAlign: "center",
          color: "text.secondary",
          mt: 2,
          fontStyle: "italic",
        }}
      >
        {missingReason}
      </Typography>
    )}
  </div>
);

export default EmailPreviewPanel;
